import * as React from 'react';
import type { ButtonType, ButtonProps, InheritedProps } from '../Button/Button';
import Button from '../Button/Button';
import PopupView from '../PopupView/PopupView';
import type { DropdownPopupContext } from '../DropdownPopup/DropdownPopup';
import DropdownPopup from '../DropdownPopup/DropdownPopup';
import IconArrowDown from '../../../assets/studio/icon-arrow-down.svg';
import './split-button.scss';

export type SplitButtonProps<T> = {
    selected?: number;
    action?: React.ReactNode;
    useDimFrame?: boolean;
    options: T[];
    buttonType?: ButtonType;
    onActionClick?(selectedOption: $TSFixMe): void;
    onMenuClicked?(event: React.MouseEvent): void;
    children: React.ReactNode | { (opts: SplitButtonChildrenProps<T>): React.ReactNode };
    actionButtonIcon?: ButtonProps['icon'];
    actionButtonIconPosition?: ButtonProps['iconPosition'];
    tooltip?: string | null;
    onSetDimmedFrame?: (state: boolean) => void;
} & Pick<ButtonProps, 'buttonType'> &
    Omit<InheritedProps, 'children'>;

export type SplitButtonChildrenProps<T> = {
    handleMenuItemClick(event: React.MouseEvent, index: number): void;
    selectedIndex: number;
    options: T[];
};

// generic + forwardRef
export interface SplitButtonWithForwardRefType extends React.FC<SplitButtonProps<string | number>> {
    <T extends string | number>(props: SplitButtonProps<T> & { ref?: React.Ref<HTMLDivElement> }): ReturnType<React.FC<SplitButtonProps<T>>>;
}

const SplitButton: SplitButtonWithForwardRefType = React.forwardRef(
    (
        {
            action,
            actionButtonIcon,
            actionButtonIconPosition = 'left',
            buttonType = 'primary',
            options = [],
            useDimFrame = false,
            onActionClick,
            onMenuClicked,
            children,
            tooltip,
            onSetDimmedFrame,
            selected,
            ...buttonProps
        },
        ref: React.Ref<HTMLDivElement>
    ) => {
        const dropdownPopupContext = React.useRef<DropdownPopupContext>(null);

        const [selectedIndex, setSelectedIndex] = React.useState(0);

        React.useEffect(() => {
            if (typeof selected === 'number') {
                setSelectedIndex(selected);
            }
        }, [selected]);

        const handleMenuItemClick = (event: React.MouseEvent, index: number) => {
            event.stopPropagation();
            setSelectedIndex(index);
            dropdownPopupContext.current?.togglePopup(false);
            toggleDimFrame(false);
        };

        const toggleDimFrame = (state: boolean) => {
            if (!useDimFrame) {
                return;
            }

            onSetDimmedFrame?.(state);
        };

        return (
            <DropdownPopup
                className="split-button"
                data-tip={tooltip}
                ref={ref}
                contextRef={dropdownPopupContext}
                placement="bottom-start"
                offset={[0, 5]}
                onToggle={(state) => {
                    toggleDimFrame(state);
                }}
                renderTrigger={(triggerOpts) => (
                    <div className="split-button-container">
                        <Button
                            buttonType={buttonType}
                            icon={actionButtonIcon}
                            iconPosition={actionButtonIconPosition}
                            className="split-button-action"
                            onClick={() => {
                                onActionClick?.(options[selectedIndex]);
                            }}
                            {...buttonProps}
                        >
                            {action ? action : options[selectedIndex]}
                        </Button>
                        <Button
                            buttonType={buttonType}
                            className="split-button-menu"
                            {...buttonProps}
                            {...triggerOpts}
                            onClick={(e) => {
                                triggerOpts.onClick?.(e);
                                onMenuClicked?.(e);
                            }}
                        >
                            <IconArrowDown />
                        </Button>
                    </div>
                )}
            >
                <PopupView className="split-button-dropdown-menu">
                    {typeof children === 'function' ? children({ handleMenuItemClick, selectedIndex, options }) : children}
                </PopupView>
            </DropdownPopup>
        );
    }
);

SplitButton.displayName = 'SplitButton';

export default SplitButton;
